import { useState, type KeyboardEvent } from 'react';
import type { components } from '@/api/schema';
import { AppIcon } from '@/components/AppIcon';
import { PackedQtyInput } from '@/components/PackedQtyInput';
import { DialogSubject, ErrorNote, FormActions, Modal, TextArea, errorMessage, quantity } from '@/components/ui';
import { useItemsById } from '@/hooks/inventory';

type DemandRow = components['schemas']['DemandItemDto'];

/** What the server takes for a decision: a comment, and on approving an amount. */
export interface DemandDecision {
  comment?: string;
  qty?: string;
}

/** As long as the server keeps a decision's comment. */
const COMMENT_MAX = 500;

/** The usual reasons for turning a demand down, one tap each. Always editable. */
const REJECT_REASONS = [
  'Enough stock on hand',
  'Already ordered',
  'Not needed now',
  'Raise again with a quantity',
];

/**
 * Deciding on a stock demand. Rejecting asks for an optional reason, with the
 * usual ones a tap away. Approving offers the amount asked for, which the
 * approver can change: asked for 80, approve 50.
 *
 * Stays open with the server's message if the decision is refused, so
 * nothing typed is lost.
 */
export function DemandDecisionDialog({
  mode,
  demand,
  onSubmit,
  onClose,
}: {
  mode: 'approve' | 'reject';
  demand: DemandRow;
  onSubmit: (decision: DemandDecision) => Promise<unknown>;
  onClose: () => void;
}) {
  const rejecting = mode === 'reject';
  const itemsById = useItemsById();
  const [comment, setComment] = useState('');
  const [qty, setQty] = useState(rejecting ? '' : (demand.qty ?? ''));
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const amount = (value: string) => `${quantity(value, demand.uomPrecision)} ${demand.uom}`;
  const changed = !rejecting && qty !== '' && (demand.qty === null || Number(qty) !== Number(demand.qty));
  const title = `${rejecting ? 'Reject' : 'Approve'} ${demand.documentNo}`;

  async function submit() {
    if (pending) return;
    if (!rejecting && qty !== '' && !(Number(qty) > 0)) {
      setProblem('Approve a quantity above zero, or leave it as asked.');
      return;
    }
    setProblem(null);
    setPending(true);
    try {
      await onSubmit({
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(changed ? { qty } : {}),
      });
      onClose();
    } catch (e) {
      setProblem(errorMessage(e, 'That did not work'));
    } finally {
      setPending(false);
    }
  }

  // Ctrl+Enter (⌘+Enter on a Mac) decides from inside the comment box.
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void submit();
    }
  }

  const counter = comment.length > COMMENT_MAX - 100 ? `${comment.length} / ${COMMENT_MAX}` : null;

  return (
    <Modal title={title} onClose={onClose}>
      <h2 className="withicon">
        <AppIcon kind={rejecting ? 'reject' : 'approve'} size={18} />
        {title}
      </h2>
      <DialogSubject
        icon="item"
        code={demand.skuCode}
        name={demand.skuName}
        nameUr={demand.skuNameUr}
        tags={[
          { icon: 'store', label: `${demand.storeCode} · ${demand.store}` },
          { label: `On hand ${amount(demand.onHand)}` },
          demand.raisedBy ? { label: `Raised by ${demand.raisedBy}` } : null,
        ]}
        figure={{
          label: 'Asked for',
          value: demand.qty ? quantity(demand.qty, demand.uomPrecision) : 'Not given',
          ...(demand.qty ? { unit: demand.uom } : {}),
        }}
        note={demand.note && `“${demand.note}”`}
      />

      <form
        className="stack"
        onKeyDown={onKeyDown}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {!rejecting && (
          <PackedQtyInput
            label={`Quantity to approve (${demand.uom})`}
            item={itemsById.get(demand.skuId)}
            itemKey={demand.skuId}
            value={qty}
            onChange={setQty}
            hint={
              changed && demand.qty
                ? differenceHint(demand, demand.qty, qty)
                : demand.qty
                  ? 'Change it to approve more or less than was asked.'
                  : 'Optional — nothing was asked for, so the amount is yours to set.'
            }
          />
        )}

        <TextArea
          label={rejecting ? 'Reason (optional)' : 'Comment (optional)'}
          value={comment}
          onChange={setComment}
          rows={2}
          maxLength={COMMENT_MAX}
          placeholder={rejecting ? 'Why it is turned down — pick one below or type your own' : 'e.g. 50 now, the rest next week'}
          hint={counter ?? (rejecting ? 'Leave it empty to reject without a reason.' : undefined)}
        />

        {rejecting && (
          <div className="settings__choices" role="group" aria-label="Common reasons">
            {REJECT_REASONS.map((reason) => {
              const on = comment.trim() === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  className={`chip ${on ? 'chip--on' : ''}`}
                  aria-pressed={on}
                  // A tap picks the reason, a second tap takes it back. Typed
                  // words are not thrown away for a chip: it is added after them.
                  onClick={() =>
                    setComment((now) => {
                      const typed = now.trim();
                      if (typed === reason) return '';
                      if (typed === '' || REJECT_REASONS.includes(typed)) return reason;
                      return `${typed}. ${reason}`.slice(0, COMMENT_MAX);
                    })
                  }
                >
                  {on ? '✓ ' : ''}
                  {reason}
                </button>
              );
            })}
          </div>
        )}

        {problem && <ErrorNote message={problem} />}

        <FormActions onCancel={onClose}>
          <button type="submit" className={`btn ${rejecting ? 'btn--danger' : 'btn--primary'}`} disabled={pending}>
            {pending ? 'Working…' : rejecting ? 'Reject' : changed ? `Approve ${amount(qty)}` : 'Approve'}
          </button>
        </FormActions>
      </form>
    </Modal>
  );
}

/** "Asked for 80 BAG — approving 50 BAG (30 fewer)". */
function differenceHint(demand: Pick<DemandRow, 'uom' | 'uomPrecision'>, asked: string, approving: string) {
  const amount = (value: string) => `${quantity(value, demand.uomPrecision)} ${demand.uom}`;
  const diff = Number(approving) - Number(asked);
  if (!(Number(approving) > 0)) return `Asked for ${amount(asked)}.`;
  const by = quantity(String(Math.abs(diff)), demand.uomPrecision);
  return `Asked for ${amount(asked)} — approving ${amount(approving)} (${by} ${diff < 0 ? 'fewer' : 'more'}).`;
}
